import { useEffect, useState, type FormEvent } from 'react'
import type { LicenseEnvelope } from '@alpha-edge/shared'
import { PageHeader, StatusBadge, btnPrimary, colors, input, label as labelStyle } from '../../components/ui'
import { api, ApiError } from '../../services/api'
import { useAuth } from '../../hooks/use-auth'

interface LicenseStatusResponse {
  readonly envelope: LicenseEnvelope | null
  readonly reportingEnabled: boolean
  readonly centralUrl: string | null
}

// One activated tenancy as returned by GET /api/license/tenancies -- the local
// binding joined with its cached license envelope status.
interface ActivatedTenancy {
  readonly id: string
  readonly tenancyId: string
  readonly name: string | null
  readonly organizationId: string
  readonly activatedAt: string
  readonly status: string | null
  readonly tier: string | null
  readonly expiresAt: string | null
}

// Customer-side license settings page. Reads the cached license envelope
// from system_config.license_status (written by the FleetReporter plugin
// on each successful heartbeat) and surfaces it to the local lab admin
// so they can see "licensed to X, expires Y, last verified Z" without
// leaving their own instance. Also lets them activate, re-key, or
// deactivate reporting entirely.
export function LicensePage() {
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin'
  const [status, setStatus] = useState<LicenseStatusResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showActivate, setShowActivate] = useState(false)
  const [form, setForm] = useState({ centralUrl: '', licenseKey: '' })
  const [submitting, setSubmitting] = useState(false)

  // Phase 2 self-service activation: the local list of activated tenancies and
  // the "add tenancy" form (admin-only).
  const [tenancies, setTenancies] = useState<ActivatedTenancy[]>([])
  const [showAddTenancy, setShowAddTenancy] = useState(false)
  const [tenancyForm, setTenancyForm] = useState({
    licenseKey: '',
    organizationName: '',
    adminEmail: '',
    adminPassword: '',
  })
  const [tenancySubmitting, setTenancySubmitting] = useState(false)
  const [tenancyError, setTenancyError] = useState<string | null>(null)

  async function refresh(): Promise<void> {
    setLoading(true)
    try {
      const res = await api.get<LicenseStatusResponse>('/license/status')
      setStatus(res.data ?? null)
      // Best-effort: the tenancy list is non-critical, so a failure here must
      // not block the license status from rendering.
      try {
        const tenRes = await api.get<ActivatedTenancy[]>('/license/tenancies')
        setTenancies(tenRes.data ?? [])
      } catch {
        setTenancies([])
      }
      setLoading(false)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load license status')
      setLoading(false)
    }
  }

  useEffect(() => {
    refresh()
  }, [])

  async function handleActivateTenancy(e: FormEvent): Promise<void> {
    e.preventDefault()
    setTenancySubmitting(true)
    setTenancyError(null)
    try {
      await api.post('/license/activate-tenancy', {
        licenseKey: tenancyForm.licenseKey.trim(),
        organizationName: tenancyForm.organizationName.trim() || undefined,
        adminEmail: tenancyForm.adminEmail.trim(),
        adminPassword: tenancyForm.adminPassword,
      })
      setShowAddTenancy(false)
      setTenancyForm({ licenseKey: '', organizationName: '', adminEmail: '', adminPassword: '' })
      await refresh()
    } catch (err) {
      // Surface Central's reason verbatim (esp. "bound to another environment").
      setTenancyError(err instanceof ApiError ? err.message : 'Tenancy activation failed')
    } finally {
      setTenancySubmitting(false)
    }
  }

  async function handleActivate(e: FormEvent): Promise<void> {
    e.preventDefault()
    setSubmitting(true)
    setError(null)
    try {
      await api.post('/license/activate', { centralUrl: form.centralUrl.trim(), licenseKey: form.licenseKey.trim() })
      setShowActivate(false)
      setForm({ centralUrl: '', licenseKey: '' })
      await refresh()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Activation failed')
    } finally {
      setSubmitting(false)
    }
  }

  async function handleDeactivate(): Promise<void> {
    if (!confirm('Deactivate license reporting? This deployment will stop sending heartbeats to central.')) return
    try {
      await api.post('/license/deactivate', {})
      await refresh()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Deactivation failed')
    }
  }

  if (loading) return <div style={{ color: colors.textMuted }}>Loading...</div>

  return (
    <div>
      <PageHeader title="License" subtitle="Local license status reported by your MedFlow central hub" />
      {error && (
        <div
          style={{
            color: colors.danger,
            padding: '0.75rem',
            background: colors.bgError,
            border: `1px solid ${colors.borderError}`,
            borderRadius: '3px',
            marginBottom: '1rem',
          }}
        >
          {error}
        </div>
      )}

      <div
        style={{
          background: colors.bgCard,
          border: `1px solid ${colors.border}`,
          borderRadius: '6px',
          padding: '1.25rem',
          maxWidth: '720px',
        }}
      >
        {!status?.reportingEnabled ? (
          <div>
            <div style={{ color: colors.textMuted, marginBottom: '0.75rem' }}>
              License reporting is <strong>not activated</strong> on this deployment. Ask your customer admin to mint a
              license key on the MedFlow central hub, then paste it here.
            </div>
            <button
              type="button"
              style={btnPrimary}
              onClick={() => setShowActivate(true)}
              data-testid="activate-button"
            >
              Activate License
            </button>
          </div>
        ) : status.envelope ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <Field label="Status">
              <StatusBadge status={status.envelope.status} />
            </Field>
            <Field label="Tier">{status.envelope.tier}</Field>
            <Field label="Customer">{status.envelope.customerName}</Field>
            <Field label="Environment">{status.envelope.environmentName}</Field>
            <Field label="Expires">
              {status.envelope.expiresAt ? new Date(status.envelope.expiresAt).toLocaleDateString() : 'No expiry'}
            </Field>
            <Field label="Days remaining">{status.envelope.daysRemaining ?? '—'}</Field>
            <Field label="Features">
              {status.envelope.features.length > 0 ? status.envelope.features.join(', ') : '—'}
            </Field>
            <Field label="Last verified">{new Date(status.envelope.lastVerifiedAt).toLocaleString()}</Field>
            <Field label="Central URL">{status.centralUrl ?? '—'}</Field>
          </div>
        ) : (
          <div style={{ color: colors.textMuted }}>
            Reporting is activated, but no heartbeat has been received yet. Status will update within one reporter
            interval.
          </div>
        )}

        {status?.reportingEnabled && (
          <div style={{ marginTop: '1.5rem', display: 'flex', gap: '0.5rem' }}>
            <button type="button" onClick={() => setShowActivate(true)}>
              Re-key
            </button>
            <button type="button" onClick={handleDeactivate}>
              Deactivate
            </button>
          </div>
        )}
      </div>

      {/* Phase 2: tenancies activated on this (shared) environment. */}
      <div
        style={{
          background: colors.bgCard,
          border: `1px solid ${colors.border}`,
          borderRadius: '6px',
          padding: '1.25rem',
          maxWidth: '720px',
          marginTop: '1rem',
        }}
      >
        <div
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}
        >
          <h3 style={{ margin: 0, color: colors.textHeading }}>Tenancies</h3>
          {isAdmin && (
            <button
              type="button"
              style={btnPrimary}
              onClick={() => setShowAddTenancy(true)}
              data-testid="add-tenancy-button"
            >
              Add tenancy
            </button>
          )}
        </div>
        <div style={{ color: colors.textMuted, fontSize: '13px', marginBottom: '0.75rem' }}>
          Each tenancy is one organization licensed to operate on this environment. Activate a tenancy by entering the
          license key issued for it on the MedFlow central hub.
        </div>
        {tenancies.length === 0 ? (
          <div style={{ color: colors.textMuted }}>No tenancies activated yet.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            <thead>
              <tr style={{ textAlign: 'left', color: colors.textMuted }}>
                <th style={{ padding: '6px 8px', borderBottom: `1px solid ${colors.border}` }}>Organization</th>
                <th style={{ padding: '6px 8px', borderBottom: `1px solid ${colors.border}` }}>Status</th>
                <th style={{ padding: '6px 8px', borderBottom: `1px solid ${colors.border}` }}>Tier</th>
                <th style={{ padding: '6px 8px', borderBottom: `1px solid ${colors.border}` }}>Activated</th>
              </tr>
            </thead>
            <tbody>
              {tenancies.map((t) => (
                <tr key={t.id}>
                  <td style={{ padding: '6px 8px', borderBottom: `1px solid ${colors.border}` }}>{t.name ?? '—'}</td>
                  <td style={{ padding: '6px 8px', borderBottom: `1px solid ${colors.border}` }}>
                    {t.status ? <StatusBadge status={t.status} /> : '—'}
                  </td>
                  <td style={{ padding: '6px 8px', borderBottom: `1px solid ${colors.border}` }}>{t.tier ?? '—'}</td>
                  <td style={{ padding: '6px 8px', borderBottom: `1px solid ${colors.border}` }}>
                    {new Date(t.activatedAt).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showAddTenancy && isAdmin && (
        <form
          onSubmit={handleActivateTenancy}
          style={{
            background: colors.bgCard,
            border: `1px solid ${colors.border}`,
            borderRadius: '6px',
            padding: '1.25rem',
            maxWidth: '560px',
            marginTop: '1rem',
          }}
        >
          <h3 style={{ marginTop: 0 }}>Activate tenancy</h3>
          {tenancyError && (
            <div
              style={{
                color: colors.danger,
                padding: '0.75rem',
                background: colors.bgError,
                border: `1px solid ${colors.borderError}`,
                borderRadius: '3px',
                marginBottom: '0.75rem',
              }}
              data-testid="tenancy-error"
            >
              {tenancyError}
            </div>
          )}
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={labelStyle}>Tenancy license key</label>
            <input
              type="text"
              required
              value={tenancyForm.licenseKey}
              onChange={(e) => setTenancyForm({ ...tenancyForm, licenseKey: e.target.value })}
              style={{ ...input, fontFamily: 'monospace' }}
              data-testid="tenancy-key-input"
            />
          </div>
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={labelStyle}>Organization name (optional)</label>
            <input
              type="text"
              placeholder="Defaults to the tenancy name on Central"
              value={tenancyForm.organizationName}
              onChange={(e) => setTenancyForm({ ...tenancyForm, organizationName: e.target.value })}
              style={input}
              data-testid="tenancy-org-name"
            />
          </div>
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={labelStyle}>Admin email</label>
            <input
              type="email"
              required
              value={tenancyForm.adminEmail}
              onChange={(e) => setTenancyForm({ ...tenancyForm, adminEmail: e.target.value })}
              style={input}
              data-testid="tenancy-admin-email"
            />
          </div>
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={labelStyle}>Admin password</label>
            <input
              type="password"
              required
              minLength={8}
              value={tenancyForm.adminPassword}
              onChange={(e) => setTenancyForm({ ...tenancyForm, adminPassword: e.target.value })}
              style={input}
              data-testid="tenancy-admin-password"
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
            <button type="button" onClick={() => setShowAddTenancy(false)}>
              Cancel
            </button>
            <button type="submit" style={btnPrimary} disabled={tenancySubmitting}>
              {tenancySubmitting ? 'Activating...' : 'Activate tenancy'}
            </button>
          </div>
        </form>
      )}

      {showActivate && (
        <form
          onSubmit={handleActivate}
          style={{
            background: colors.bgCard,
            border: `1px solid ${colors.border}`,
            borderRadius: '6px',
            padding: '1.25rem',
            maxWidth: '560px',
            marginTop: '1rem',
          }}
        >
          <h3 style={{ marginTop: 0 }}>Activate license</h3>
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={labelStyle}>Central URL</label>
            <input
              type="url"
              required
              placeholder="https://fleet.medflow.example.com"
              value={form.centralUrl}
              onChange={(e) => setForm({ ...form, centralUrl: e.target.value })}
              style={input}
              data-testid="license-central-url"
            />
          </div>
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={labelStyle}>License key</label>
            <input
              type="text"
              required
              value={form.licenseKey}
              onChange={(e) => setForm({ ...form, licenseKey: e.target.value })}
              style={{ ...input, fontFamily: 'monospace' }}
              data-testid="license-key-input"
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
            <button type="button" onClick={() => setShowActivate(false)}>
              Cancel
            </button>
            <button type="submit" style={btnPrimary} disabled={submitting}>
              {submitting ? 'Activating...' : 'Activate'}
            </button>
          </div>
        </form>
      )}
    </div>
  )
}

function Field({ label, children }: { readonly label: string; readonly children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: '12px', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </div>
      <div style={{ fontSize: '15px', color: colors.textHeading, marginTop: '4px' }}>{children}</div>
    </div>
  )
}
